import React, { useCallback, useEffect, useRef, useState } from "react";
import { Modal, Button, Select, Slider, Spin, notification, Tooltip, Progress } from "antd";
import {
  RotateLeftOutlined,
  RotateRightOutlined,
  SwapOutlined,
  VerticalAlignMiddleOutlined,
  ThunderboltOutlined,
  RobotOutlined,
  UndoOutlined,
  ScissorOutlined,
} from "@ant-design/icons";
import Cropper from "react-easy-crop";
import "react-easy-crop/react-easy-crop.css";
import {
  PRESET_OPTIONS,
  getAspectPreset,
} from "lib/imageStudio/aspectPresets";
import {
  DEFAULT_ADJUSTMENTS,
  loadImageSource,
  exportEditedImage,
  blobToImageFile,
  computeAutoEnhanceAdjustmentsForQuality,
  analyzeImageForEnhance,
  renderAdjustedImagePreview,
  adjustmentsAreDefault,
} from "lib/imageStudio/imageStudioUtils";
import { imageStudioClient, fileToBase64 } from "lib/imageStudio/imageStudioClient";
import { removeImageBackground } from "lib/imageStudio/removeBackground";
import "./ImageStudioModal.css";

function formatSliderValue(v) {
  return `${Math.round(v)}%`;
}

export default function ImageStudioModal({
  open,
  source,
  aspectPreset = "free",
  contextLabel,
  onCancel,
  onComplete,
}) {
  const [imageUrl, setImageUrl] = useState(null);
  const [fileName, setFileName] = useState("image.jpg");
  const [loadingSource, setLoadingSource] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [presetId, setPresetId] = useState(aspectPreset);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [flip, setFlip] = useState({ horizontal: false, vertical: false });
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [adjustments, setAdjustments] = useState({ ...DEFAULT_ADJUSTMENTS });
  const [suggestedAlt, setSuggestedAlt] = useState("");
  const [loadedBlob, setLoadedBlob] = useState(null);
  const [corsRisk, setCorsRisk] = useState(false);
  const [bgRemoved, setBgRemoved] = useState(false);
  const [bgRemoving, setBgRemoving] = useState(false);
  const [bgRemoveProgress, setBgRemoveProgress] = useState(0);
  const initialImageRef = useRef(null);
  const activeBlobUrlRef = useRef(null);
  const previewBlobUrlRef = useRef(null);
  const [previewImageUrl, setPreviewImageUrl] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  const preset = getAspectPreset(presetId);
  const aspect = preset.aspect;
  const useFullImageMode = presetId === "original" || !!preset.skipCrop;
  // Allow zooming out so the whole photo can fit inside a free / full-image frame.
  const minZoom = useFullImageMode || presetId === "free" || aspect == null ? 0.4 : 1;
  const maxZoom = 3;

  useEffect(() => {
    if (open) {
      setPresetId(aspectPreset || "free");
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setRotation(0);
      setFlip({ horizontal: false, vertical: false });
      setAdjustments({ ...DEFAULT_ADJUSTMENTS });
      setSuggestedAlt("");
      setLoadedBlob(null);
      setCorsRisk(false);
      setBgRemoved(false);
      setBgRemoving(false);
      setBgRemoveProgress(0);
      initialImageRef.current = null;
      if (previewBlobUrlRef.current) {
        URL.revokeObjectURL(previewBlobUrlRef.current);
        previewBlobUrlRef.current = null;
      }
      setPreviewImageUrl(null);
      setPreviewLoading(false);
    }
  }, [open, aspectPreset, source]);

  useEffect(() => {
    let revokeUrl = null;
    if (!open || !source) {
      setImageUrl(null);
      return undefined;
    }

    let cancelled = false;
    (async () => {
      setLoadingSource(true);
      try {
        const loaded = await loadImageSource(source);
        if (cancelled || !loaded) return;
        revokeUrl = loaded.url.startsWith("blob:") ? loaded.url : null;
        activeBlobUrlRef.current = revokeUrl;
        initialImageRef.current = {
          url: loaded.url,
          blob: loaded.blob || null,
          fileName: loaded.fileName || "image.jpg",
        };
        setImageUrl(loaded.url);
        setFileName(loaded.fileName || "image.jpg");
        setLoadedBlob(loaded.blob || null);
        setCorsRisk(!!loaded.corsRisk);
      } catch {
        if (!cancelled) {
          notification.error({ message: "Could not load image for editing" });
        }
      } finally {
        if (!cancelled) setLoadingSource(false);
      }
    })();

    return () => {
      cancelled = true;
      if (revokeUrl) URL.revokeObjectURL(revokeUrl);
      if (activeBlobUrlRef.current && activeBlobUrlRef.current !== revokeUrl) {
        URL.revokeObjectURL(activeBlobUrlRef.current);
      }
      activeBlobUrlRef.current = null;
    };
  }, [open, source]);

  useEffect(() => {
    if (!open || !imageUrl || corsRisk) {
      if (previewBlobUrlRef.current) {
        URL.revokeObjectURL(previewBlobUrlRef.current);
        previewBlobUrlRef.current = null;
      }
      setPreviewImageUrl(null);
      setPreviewLoading(false);
      return undefined;
    }

    if (adjustmentsAreDefault(adjustments)) {
      if (previewBlobUrlRef.current) {
        URL.revokeObjectURL(previewBlobUrlRef.current);
        previewBlobUrlRef.current = null;
      }
      setPreviewImageUrl(null);
      setPreviewLoading(false);
      return undefined;
    }

    let cancelled = false;
    setPreviewLoading(true);
    const timer = setTimeout(async () => {
      try {
        const blob = await renderAdjustedImagePreview(imageUrl, adjustments);
        if (cancelled) return;
        if (previewBlobUrlRef.current) {
          URL.revokeObjectURL(previewBlobUrlRef.current);
        }
        const url = URL.createObjectURL(blob);
        previewBlobUrlRef.current = url;
        setPreviewImageUrl(url);
      } catch {
        if (!cancelled) setPreviewImageUrl(null);
      } finally {
        if (!cancelled) setPreviewLoading(false);
      }
    }, 350);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [open, imageUrl, adjustments, corsRisk]);

  const cropperImage = previewImageUrl || imageUrl;
  const cropperMediaStyle =
    !previewImageUrl && !adjustmentsAreDefault(adjustments)
      ? {
          filter: `brightness(${(adjustments.brightness ?? 100) / 100}) contrast(${(adjustments.contrast ?? 100) / 100}) saturate(${(adjustments.saturation ?? 100) / 100})`,
        }
      : undefined;

  const resolveProcessingBlob = async () => {
    if (loadedBlob) {
      return loadedBlob instanceof File
        ? loadedBlob
        : new File([loadedBlob], fileName, { type: loadedBlob.type || "image/jpeg" });
    }
    if (source instanceof File) return source;
    if (source instanceof Blob) {
      return new File([source], fileName, { type: source.type || "image/jpeg" });
    }
    const res = await fetch(imageUrl);
    const blob = await res.blob();
    return new File([blob], fileName, { type: blob.type || "image/jpeg" });
  };

  const replaceActiveImage = (blob, nextFileName) => {
    const nextUrl = URL.createObjectURL(blob);
    if (activeBlobUrlRef.current?.startsWith("blob:")) {
      URL.revokeObjectURL(activeBlobUrlRef.current);
    }
    activeBlobUrlRef.current = nextUrl;
    setImageUrl(nextUrl);
    setLoadedBlob(blob);
    setFileName(nextFileName);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setCroppedAreaPixels(null);
  };

  const restoreOriginalBackground = () => {
    const orig = initialImageRef.current;
    if (!orig || !bgRemoved) return;

    if (orig.blob) {
      replaceActiveImage(orig.blob, orig.fileName || "image.jpg");
    } else {
      if (
        activeBlobUrlRef.current?.startsWith("blob:") &&
        activeBlobUrlRef.current !== orig.url
      ) {
        URL.revokeObjectURL(activeBlobUrlRef.current);
      }
      activeBlobUrlRef.current = orig.url.startsWith("blob:") ? orig.url : null;
      setImageUrl(orig.url);
      setLoadedBlob(null);
      setFileName(orig.fileName || "image.jpg");
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setCroppedAreaPixels(null);
    }
    setBgRemoved(false);
    notification.info({ message: "Original background restored" });
  };

  const onCropAreaChange = useCallback((_, pixels) => {
    if (pixels?.width && pixels?.height) {
      setCroppedAreaPixels(pixels);
    }
  }, []);

  const onCropComplete = useCallback((_, pixels) => {
    if (pixels?.width && pixels?.height) {
      setCroppedAreaPixels(pixels);
    }
  }, []);

  const updateAdjustment = (key, value) => {
    setAdjustments((prev) => ({ ...prev, [key]: value }));
  };

  const resetAdjustments = () => {
    setAdjustments({ ...DEFAULT_ADJUSTMENTS });
    setSuggestedAlt("");
  };

  const applyAutoEnhance = async (opts = {}) => {
    if (!imageUrl) return;
    const isProduct =
      presetId === "product" ||
      (contextLabel && /product|cover|gallery/i.test(contextLabel));
    try {
      const quality = await analyzeImageForEnhance(imageUrl);
      setAdjustments(computeAutoEnhanceAdjustmentsForQuality(quality, { isProduct }));
    } catch {
      setAdjustments(computeAutoEnhanceAdjustmentsForQuality(null, { isProduct }));
    }
    if (!opts.silent) {
      notification.success({
        message: "Auto enhance applied",
        description: "Adjust sliders if needed - sharpness helps soft or small images.",
      });
    }
  };

  const runKaiEnhance = async () => {
    if (!imageUrl) return;
    setAiLoading(true);
    try {
      const file = await resolveProcessingBlob();
      const base64 = await fileToBase64(file);

      const result = await imageStudioClient.enhance({
        imageBase64: base64,
        mimeType: file.type || "image/jpeg",
        context: contextLabel || preset.hint || "",
        aspectPreset: presetId,
      });

      if (result?.success && result?.data?.adjustments) {
        setAdjustments((prev) => ({
          ...prev,
          ...result.data.adjustments,
        }));
        if (result.data.altText) setSuggestedAlt(result.data.altText);
        notification.success({
          message: "KAI enhancement applied",
          description: result.data.tip || "Fine-tune with the sliders below.",
        });
      } else {
        await applyAutoEnhance({ silent: true });
        notification.info({ message: "KAI unavailable - applied local auto enhance" });
      }
    } catch (e) {
      await applyAutoEnhance({ silent: true });
      if (e?.status === 429) {
        notification.warning({ message: "AI rate limit - applied local auto enhance instead" });
      } else {
        notification.info({ message: "KAI unavailable - applied local auto enhance" });
      }
    } finally {
      setAiLoading(false);
    }
  };

  const runRemoveBackground = async () => {
    if (!imageUrl || corsRisk || bgRemoved) return;
    setBgRemoving(true);
    setBgRemoveProgress(0);
    try {
      const input = await resolveProcessingBlob();
      const resultBlob = await removeImageBackground(input, {
        onProgress: setBgRemoveProgress,
      });
      const nextName = `${(fileName || "image").replace(/\.[^.]+$/, "") || "image"}.png`;
      replaceActiveImage(resultBlob, nextName);
      setBgRemoved(true);
      notification.success({
        message: "Background removed",
        description: "Transparent areas show in the editor. Apply saves as JPEG on white for your storefront.",
      });
    } catch (e) {
      notification.error({
        message: "Could not remove background",
        description: e?.message || "Try again or use a different image.",
      });
    } finally {
      setBgRemoving(false);
      setBgRemoveProgress(0);
    }
  };

  const handlePresetChange = (nextId) => {
    setPresetId(nextId);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    if (nextId === "original") {
      setRotation(0);
      setFlip({ horizontal: false, vertical: false });
    }
  };

  const handleApply = async (opts = {}) => {
    if (!imageUrl) {
      notification.error({ message: "Wait for the image to load before applying" });
      return;
    }
    const forceFullImage = !!opts.forceFullImage;
    const useFullImage = forceFullImage || presetId === "original" || !!preset.skipCrop;
    if (!useFullImage && !croppedAreaPixels) {
      notification.error({ message: "Wait for the image to load before applying" });
      return;
    }
    setExporting(true);
    try {
      const effectiveRotation = useFullImage && forceFullImage ? 0 : rotation;
      const effectiveFlip =
        useFullImage && forceFullImage ? { horizontal: false, vertical: false } : flip;
      const noTransforms =
        adjustmentsAreDefault(adjustments) &&
        effectiveRotation === 0 &&
        !effectiveFlip.horizontal &&
        !effectiveFlip.vertical &&
        !bgRemoved;

      let file;
      if (useFullImage && noTransforms && loadedBlob) {
        file = blobToImageFile(loadedBlob, fileName || "image.jpg");
      } else {
        file = await exportEditedImage({
          imageSrc: imageUrl,
          pixelCrop: croppedAreaPixels,
          rotation: effectiveRotation,
          flip: effectiveFlip,
          adjustments,
          fileName,
          preferPng: bgRemoved,
          skipCrop:
            useFullImage &&
            effectiveRotation === 0 &&
            !effectiveFlip.horizontal &&
            !effectiveFlip.vertical,
        });
      }

      const appliedPreset = useFullImage ? "original" : presetId;
      if (forceFullImage) setPresetId("original");

      const minW = preset.minWidth;
      const minH = preset.minHeight;
      const cropW = useFullImage ? null : croppedAreaPixels?.width;
      const cropH = useFullImage ? null : croppedAreaPixels?.height;
      if (minW && minH && cropW && cropH && (cropW < minW * 0.5 || cropH < minH * 0.5)) {
        notification.warning({
          message: "Low resolution",
          description: `For best results on the storefront, use at least ${minW}×${minH}px.`,
        });
      }

      onComplete?.(file, { altText: suggestedAlt, aspectPreset: appliedPreset });
    } catch (e) {
      notification.error({
        message: "Could not export image",
        description: e?.message || "Try uploading a different file.",
      });
    } finally {
      setExporting(false);
    }
  };

  const title = contextLabel ? `Edit image - ${contextLabel}` : "Edit image";

  return (
    <Modal
      title={title}
      open={open}
      onCancel={onCancel}
      width={960}
      centered
      zIndex={1450}
      getContainer={() => document.body}
      className="image-studio-modal"
      destroyOnClose
      footer={null}
      maskClosable={false}
    >
      <div className="image-studio">
        <div className="image-studio__main">
          <div
            className={`image-studio__crop-wrap${bgRemoved ? " image-studio__crop-wrap--transparent" : ""}`}
          >
            {(loadingSource || !imageUrl) && !bgRemoving && (
              <div className="image-studio__loading">
                <Spin size="large" tip="Loading image…" />
              </div>
            )}
            {bgRemoving && (
              <div className="image-studio__loading">
                <div style={{ textAlign: "center", color: "#fff", width: "80%", maxWidth: 280 }}>
                  <Spin size="large" />
                  <div style={{ marginTop: 12, fontSize: 13 }}>Removing background…</div>
                  {bgRemoveProgress > 0 && (
                    <Progress
                      percent={bgRemoveProgress}
                      size="small"
                      showInfo
                      style={{ marginTop: 8 }}
                    />
                  )}
                </div>
              </div>
            )}
            {imageUrl && useFullImageMode && (
              <div className="image-studio__full-preview">
                {/* eslint-disable-next-line jsx-a11y/alt-text */}
                <img src={cropperImage || imageUrl} alt="" />
                <div className="image-studio__full-preview-badge">Full image — no crop</div>
              </div>
            )}
            {imageUrl && !useFullImageMode && (
              <Cropper
                image={cropperImage}
                crop={crop}
                zoom={zoom}
                minZoom={minZoom}
                maxZoom={maxZoom}
                rotation={rotation}
                aspect={aspect}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onRotationChange={setRotation}
                onCropComplete={onCropComplete}
                onCropAreaChange={onCropAreaChange}
                mediaStyle={cropperMediaStyle}
                objectFit={aspect == null ? "contain" : "cover"}
                restrictPosition={aspect != null}
                roundCropAreaPixels
                showGrid
                style={{ containerStyle: { background: "#111" } }}
              />
            )}
            {previewLoading && (
              <div
                className="image-studio__loading"
                style={{ background: "rgba(0,0,0,0.25)", pointerEvents: "none" }}
              >
                <Spin size="small" tip="Updating preview…" />
              </div>
            )}
          </div>

          <aside className="image-studio__sidebar">
            {corsRisk && (
              <div className="image-studio__hint" style={{ color: "#d4380d", marginBottom: 12 }}>
                This image could not be loaded for editing. Try uploading the file directly from your computer.
              </div>
            )}
            <div className="image-studio__control">
              <span className="image-studio__label">How do you want to crop?</span>
              <span className="image-studio__hint">{preset.hint}</span>
              <div className="image-studio__quick-presets">
                <Button
                  size="small"
                  type={presetId === "original" ? "primary" : "default"}
                  onClick={() => handlePresetChange("original")}
                >
                  Full image
                </Button>
                <Button
                  size="small"
                  type={presetId === "free" ? "primary" : "default"}
                  onClick={() => handlePresetChange("free")}
                >
                  Free crop
                </Button>
                <Button
                  size="small"
                  type={presetId === "banner" ? "primary" : "default"}
                  onClick={() => handlePresetChange("banner")}
                >
                  Banner 16:9
                </Button>
                <Button
                  size="small"
                  type={presetId === "product" ? "primary" : "default"}
                  onClick={() => handlePresetChange("product")}
                >
                  Product 3:4
                </Button>
              </div>
              <Select
                value={presetId}
                onChange={handlePresetChange}
                options={PRESET_OPTIONS}
                style={{ width: "100%", marginTop: 8 }}
                size="small"
                listHeight={320}
                getPopupContainer={() => document.body}
                dropdownStyle={{ zIndex: 1600 }}
                placeholder="More crop shapes…"
              />
            </div>

            {!useFullImageMode && (
            <div className="image-studio__control">
              <span className="image-studio__label">Zoom</span>
              <Slider min={minZoom} max={maxZoom} step={0.05} value={zoom} onChange={setZoom} />
              {(presetId === "free" || aspect == null) && (
                <span className="image-studio__hint" style={{ marginBottom: 0 }}>
                  Zoom out to fit more of the photo, then drag the crop box.
                </span>
              )}
            </div>
            )}

            {!useFullImageMode && (
            <div className="image-studio__control">
              <div className="image-studio__control-row">
                <span>Rotate</span>
                <span>{rotation}°</span>
              </div>
              <Slider min={0} max={360} value={rotation} onChange={setRotation} />
              <div style={{ display: "flex", gap: 4, marginTop: 4 }}>
                <Button
                  size="small"
                  icon={<RotateLeftOutlined />}
                  onClick={() => setRotation((r) => (r - 90 + 360) % 360)}
                />
                <Button
                  size="small"
                  icon={<RotateRightOutlined />}
                  onClick={() => setRotation((r) => (r + 90) % 360)}
                />
                <Tooltip title="Flip horizontal">
                  <Button
                    size="small"
                    icon={<SwapOutlined />}
                    onClick={() => setFlip((f) => ({ ...f, horizontal: !f.horizontal }))}
                  />
                </Tooltip>
                <Tooltip title="Flip vertical">
                  <Button
                    size="small"
                    icon={<VerticalAlignMiddleOutlined />}
                    onClick={() => setFlip((f) => ({ ...f, vertical: !f.vertical }))}
                  />
                </Tooltip>
              </div>
            </div>
            )}

            {["brightness", "contrast", "saturation"].map((key) => (
              <div className="image-studio__control" key={key}>
                <div className="image-studio__control-row">
                  <span style={{ textTransform: "capitalize" }}>{key}</span>
                  <span>{formatSliderValue(adjustments[key])}</span>
                </div>
                <Slider
                  min={50}
                  max={150}
                  value={adjustments[key]}
                  onChange={(v) => updateAdjustment(key, v)}
                />
              </div>
            ))}

            <div className="image-studio__control">
              <div className="image-studio__control-row">
                <span>Sharpness</span>
                <span>{adjustments.sharpness}%</span>
              </div>
              <Slider
                min={0}
                max={100}
                value={adjustments.sharpness}
                onChange={(v) => updateAdjustment("sharpness", v)}
              />
              <span className="image-studio__hint" style={{ marginBottom: 0 }}>
                Helps soft or pixelated photos. Very blurry originals may still need a higher-res source.
              </span>
            </div>

            {suggestedAlt && (
              <div className="image-studio__control">
                <span className="image-studio__label">Suggested alt text</span>
                <span className="image-studio__hint">{suggestedAlt}</span>
              </div>
            )}
          </aside>
        </div>

        <div className="image-studio__footer">
          <div className="image-studio__footer-left">
            <Button icon={<UndoOutlined />} onClick={resetAdjustments}>
              Reset
            </Button>
            <Button icon={<ThunderboltOutlined />} onClick={applyAutoEnhance}>
              Auto enhance
            </Button>
            <Button
              icon={<RobotOutlined />}
              loading={aiLoading}
              onClick={runKaiEnhance}
            >
              Enhance with KAI
            </Button>
            <Tooltip title="AI removes the background (transparent PNG)">
              <Button
                icon={<ScissorOutlined />}
                loading={bgRemoving}
                disabled={corsRisk || bgRemoved || !imageUrl}
                onClick={runRemoveBackground}
              >
                Remove background
              </Button>
            </Tooltip>
            {bgRemoved && (
              <Button onClick={restoreOriginalBackground}>Restore background</Button>
            )}
          </div>
          <div className="image-studio__footer-right">
            <Button onClick={onCancel}>Cancel</Button>
            <Button
              loading={exporting}
              disabled={corsRisk || !imageUrl || !loadedBlob}
              onClick={() => handleApply({ forceFullImage: true })}
            >
              Use full image
            </Button>
            <Button
              type="primary"
              loading={exporting}
              disabled={corsRisk || !imageUrl || (!useFullImageMode && !croppedAreaPixels)}
              onClick={() => handleApply()}
            >
              Apply & use image
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
}
